/**
 * The event description as rich-text HTML — what the editor saves, and what the pages render.
 *
 * The service owns the format (AWSECEventHub service/EventDescriptionHtml): it sanitizes every
 * write to an allowlist — paragraphs, headings, bold / italic / underline / strike, lists,
 * blockquotes, links, line breaks — and stores a file uploaded for the event by its id alone: an
 * image as `<img data-file-id="…">`, a link to one as `<a data-file-id="…">`, dropping any `src` /
 * `href`. On read each comes back with a presigned URL that works for about an hour. So:
 *
 *   - on save ({@link descriptionForSave}) the `src`s are stripped here too. The service would drop
 *     them anyway, but a presigned URL is ~1 kB and the wire limit is 50,000 characters, so sending
 *     them would eat the budget a few images at a time;
 *   - on render ({@link sanitizeDescription}) the HTML is sanitized again with DOMPurify, to the same
 *     allowlist. Neither side relies on the other: the stored HTML predates this page, and this page
 *     must not be the reason a bad description runs script.
 *
 * Images are uploaded through the service (`uploadEventFile`) before they are inserted, so an
 * image in the editor always has an id. The upload itself takes any file up to 20 MiB; the editor's
 * Image button is narrower ({@link checkImage}) — PNG / JPEG / GIF / WebP up to 5 MiB, never SVG — and
 * says why before any request.
 *
 * Here rather than in the components because the coverage config measures only `.ts`.
 */
import DOMPurify from 'dompurify';

/** The largest image the editor inserts (the service takes files up to 20 MiB). */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** The picture formats the editor inserts — the ones the service shows in place (not SVG). */
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** What the file picker should offer. */
export const IMAGE_ACCEPT = IMAGE_TYPES.join(',');

export type ImageCheck = { ok: true } | { ok: false; reason: string };

/** Whether the editor will insert this file as an image, and why not. */
export function checkImage(file: { type: string; size: number }): ImageCheck {
  if (!IMAGE_TYPES.includes(file.type)) {
    return { ok: false, reason: 'Only PNG, JPEG, GIF or WebP images can be inserted.' };
  }
  if (file.size === 0) return { ok: false, reason: 'The image is empty.' };
  if (file.size > MAX_IMAGE_BYTES) return { ok: false, reason: 'Images must be 5 MB or smaller.' };
  return { ok: true };
}

function parse(html: string): HTMLElement {
  return new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body;
}

/** True when the HTML shows nothing: no text and no image (the editor's empty state is `<p></p>`). */
export function isEmptyDescription(html: string | undefined): boolean {
  if (!html) return true;
  const body = parse(html);
  return !body.textContent?.trim() && !body.querySelector('img');
}

/**
 * The editor's HTML as UpdateEvent should receive it: each uploaded file reduced to its id, and
 * `undefined` when the description is empty (the form then clears it, see utils/clearFields).
 */
export function descriptionForSave(html: string): string | undefined {
  if (isEmptyDescription(html)) return undefined;
  const body = parse(html);
  for (const img of Array.from(body.querySelectorAll('img'))) {
    if (img.getAttribute('data-file-id')) {
      img.removeAttribute('src');
    } else {
      // Not one of the event's uploads — the service would drop it, so do not pretend it is kept.
      img.remove();
    }
  }
  // A link to an uploaded file keeps its id; the presigned href it was read with expires.
  for (const a of Array.from(body.querySelectorAll('a[data-file-id]'))) a.removeAttribute('href');
  // Dropping images that are not uploads may have left nothing to show: that is a clear, too.
  return isEmptyDescription(body.innerHTML) ? undefined : body.innerHTML;
}

const purify = DOMPurify();

/* Links leave the page safely; an image shows only from an https URL (the presigned S3 one). Hooks
   are per instance, so this does not change DOMPurify for anyone else. */
purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
  if (node.tagName === 'IMG' && !/^https:\/\//i.test(node.getAttribute('src') ?? '')) {
    node.removeAttribute('src');
  }
  // An image's size, as the editor saves it and the service keeps it: whole pixels only.
  if (node.tagName === 'IMG') {
    for (const dim of ['width', 'height']) {
      const v = node.getAttribute(dim);
      if (v !== null && !/^[1-9][0-9]{0,3}$/.test(v)) node.removeAttribute(dim);
    }
  }
});

const ALLOWED_TAGS = [
  'p', 'br', 'h1', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li', 'blockquote', 'a', 'img',
];

/** The description, safe to set as innerHTML. */
export function sanitizeDescription(html: string | undefined): string {
  if (!html) return '';
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'data-file-id', 'width', 'height'],
    ALLOW_DATA_ATTR: false,
  });
}
